import { NavigationMenu } from "@base-ui/react/navigation-menu";
import React, {
  forwardRef,
  type HTMLAttributes,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  filterItems,
  filterSidebarItems,
  formatSearchItems,
} from "../../utils/functions";
import { ScrollArea } from "../ScrollArea";
import { useMenuState } from "./hooks/useMenuState";
import { MenuContent } from "./MenuContent";
import { MenuHeader } from "./MenuHeader";
import { MenuItem } from "./MenuItem";
import { MenuSearch } from "./MenuSearch";
import { MenuProvider } from "./menuContext";
import {
  menuBodyClass,
  menuContainerClass,
  menuContainerOpenClass,
  menuFooterClass,
  menuPopupWrapperClass,
  menuViewportClass,
} from "./styles.css";
import type { MenuItemProps, MenuObjectProps } from "./types";

interface BodyNavigationMenuProps {
  bodyItems: MenuItemProps[];
  renderMenuItem: (item: MenuItemProps, index: number) => React.ReactNode;
  searchInput: string;
  onSearchChange: (value: string) => void;
  onActiveSearchChange: (value: string) => void;
  searchItems: MenuItemProps[];
  searchPlaceholder?: string;
  searchEmptyMessage?: string;
}

const BodyNavigationMenu: React.FC<BodyNavigationMenuProps> = ({
  bodyItems,
  renderMenuItem,
  searchInput,
  onSearchChange,
  onActiveSearchChange,
  searchItems,
  searchPlaceholder,
  searchEmptyMessage,
}) => {
  const [navValue, setNavValue] = useState("");
  const searchInputFocusedRef = useRef(false);

  const handleInputFocus = useCallback(() => {
    searchInputFocusedRef.current = true;
    setNavValue("search");
  }, []);

  const handleInputBlur = useCallback((popupHovered: boolean) => {
    searchInputFocusedRef.current = false;
    if (!popupHovered) setNavValue("");
  }, []);

  return (
    <NavigationMenu.Root
      delay={0}
      closeDelay={100}
      orientation="vertical"
      value={navValue}
      onValueChange={(val) => {
        if (!val && searchInputFocusedRef.current) return;
        setNavValue(val);
      }}
    >
      <NavigationMenu.List>
        <MenuSearch
          sideBarOpen={false}
          searchInput={searchInput}
          onSearchChange={onSearchChange}
          onActiveSearchChange={onActiveSearchChange}
          searchItems={searchItems}
          searchPlaceholder={searchPlaceholder}
          searchEmptyMessage={searchEmptyMessage}
          onInputFocus={handleInputFocus}
          onInputBlur={handleInputBlur}
        />
        {bodyItems.map(renderMenuItem)}
      </NavigationMenu.List>
      <NavigationMenu.Portal>
        <NavigationMenu.Positioner
          sideOffset={4}
          side="right"
          align="start"
          collisionAvoidance={{ align: "none" }}
          style={{ zIndex: 10 }}
        >
          <NavigationMenu.Popup className={menuPopupWrapperClass}>
            <NavigationMenu.Viewport className={menuViewportClass} />
          </NavigationMenu.Popup>
        </NavigationMenu.Positioner>
      </NavigationMenu.Portal>
    </NavigationMenu.Root>
  );
};

export interface CollapsableMenuComponentProps
  extends HTMLAttributes<HTMLDivElement> {
  menuObject: MenuObjectProps;
  defaultSideBarOpen?: boolean;
  onSideBarToggle?: (e: boolean) => void;
  subitemComponent?: React.ElementType;
  searchPlaceholder?: string;
  searchEmptyMessage?: string;
}

export const CollapsableMenu = forwardRef<
  HTMLDivElement,
  CollapsableMenuComponentProps
>(
  (
    {
      menuObject,
      defaultSideBarOpen,
      onSideBarToggle,
      subitemComponent = "a",
      searchPlaceholder = "Buscar",
      searchEmptyMessage = "Nenhum resultado encontrado",
      ...props
    },
    ref,
  ) => {
    const { state, actions } = useMenuState(
      defaultSideBarOpen,
      onSideBarToggle,
    );

    const itemsFormattedToSearch = useMemo(
      () => formatSearchItems(menuObject.categories),
      [menuObject.categories],
    );

    const searchItemsFiltered = useMemo(
      () =>
        itemsFormattedToSearch.filter((item) =>
          filterItems(item, state.searchInput),
        ),
      [itemsFormattedToSearch, state.searchInput],
    );

    const { footerItems, bodyItems } = useMemo(() => {
      const filtered = filterSidebarItems(menuObject.categories);
      return {
        footerItems: filtered.filter((item) => item.barPosition === "footer"),
        bodyItems: filtered.filter((item) => !item.barPosition),
      };
    }, [menuObject.categories]);

    const renderMenuItem = useCallback(
      (item: MenuItemProps, index: number) => (
        <MenuItem
          key={`menu-item${item.path || ""}-${index}`}
          item={item}
          sideBarOpen={state.sideBarOpen}
          categoryOpen={state.categoryOpen}
          toggleCategoryOpen={(category) =>
            actions.setCategoryOpen(
              state.categoryOpen === category ? null : category,
            )
          }
        />
      ),
      [state.sideBarOpen, state.categoryOpen, actions],
    );

    return (
      <MenuProvider component={subitemComponent}>
        <div
          ref={ref}
          className={`${menuContainerClass} ${
            state.sideBarOpen ? menuContainerOpenClass : ""
          }`}
          role="navigation"
          aria-label="Menu principal"
          {...props}
        >
          <MenuHeader
            sideBarOpen={state.sideBarOpen}
            profile={menuObject.profile}
            onToggleSidebar={actions.toggleSidebar}
          />

          <div className={menuBodyClass}>
            {state.sideBarOpen ? (
              <ScrollArea maxHeight="calc(100vh - 48px)">
                <MenuSearch
                  sideBarOpen={state.sideBarOpen}
                  searchInput={state.searchInput}
                  onSearchChange={actions.setSearchInput}
                  onActiveSearchChange={actions.setActiveSearch}
                  searchItems={itemsFormattedToSearch}
                  searchPlaceholder={searchPlaceholder}
                  searchEmptyMessage={searchEmptyMessage}
                />

                <MenuContent
                  activeSearch={state.activeSearch}
                  sideBarOpen={state.sideBarOpen}
                  searchItemsFiltered={searchItemsFiltered}
                  bodyItems={bodyItems}
                  onSearchChange={actions.setActiveSearch}
                  renderMenuItem={renderMenuItem}
                />
              </ScrollArea>
            ) : (
              <BodyNavigationMenu
                bodyItems={bodyItems}
                renderMenuItem={renderMenuItem}
                searchInput={state.searchInput}
                onSearchChange={actions.setSearchInput}
                onActiveSearchChange={actions.setActiveSearch}
                searchItems={itemsFormattedToSearch}
                searchPlaceholder={searchPlaceholder}
                searchEmptyMessage={searchEmptyMessage}
              />
            )}
          </div>

          <div className={menuFooterClass}>
            {state.sideBarOpen ? (
              footerItems.map(renderMenuItem)
            ) : (
              <NavigationMenu.Root
                delay={100}
                closeDelay={150}
                orientation="vertical"
              >
                <NavigationMenu.List>
                  {footerItems.map(renderMenuItem)}
                </NavigationMenu.List>
                <NavigationMenu.Portal>
                  <NavigationMenu.Positioner
                    sideOffset={4}
                    side="right"
                    align="start"
                    collisionAvoidance={{ align: "none" }}
                  >
                    <NavigationMenu.Popup className={menuPopupWrapperClass}>
                      <NavigationMenu.Viewport className={menuViewportClass} />
                    </NavigationMenu.Popup>
                  </NavigationMenu.Positioner>
                </NavigationMenu.Portal>
              </NavigationMenu.Root>
            )}
          </div>
        </div>
      </MenuProvider>
    );
  },
);

CollapsableMenu.displayName = "CollapsableMenu";
